import { ChatViewMode } from 'enums/ChatViewMode';
import { OverlayType } from 'enums/OverlayType';
import { StorageKey } from 'enums/StorageKey';
import { TabView } from 'enums/TabView';
import { ContextMenuData } from 'interfaces/ContextMenuData';
import { DialogAggregateInfo } from 'interfaces/DialogAggregateInfo';
import { DrawerData } from 'interfaces/DrawerData';
import { HoveringButtonInfo } from 'interfaces/HoveringButtonInfo';
import { ImagesDisplayData } from 'interfaces/ImageData';
import { MessageData } from 'interfaces/MessageData';
import { OverlayProps } from 'interfaces/OverlayProps';
import { QuickActionButtonData } from 'interfaces/QuickActionButtonData';
import { QuoteData } from 'interfaces/QuoteData';
import { Action } from 'redux';
import { _counterLog } from 'utils/_counterLog';

import {
    BlinkMessageAction,
    ChatActionType,
    ContextMenuAction,
    DrawerAction,
    GoToSearchResultAction,
    MessagesHtmlRequestAction,
    OpenMediaGalleryAction,
    SearchRequestAction,
    SetActiveTabAction,
    SetAgentTypingAction,
    SetBackgroundOperationAction,
    SetBlurDelay,
    SetChatFullScreenAction,
    SetChatMaximizedAction,
    SetChatOpenAction,
    SetChatOverlayAction,
    SetChatViewModeAction,
    SetHoveringButtonsAction,
    SetIsHoveringButtonsVisibleAction,
    SetIsScrolling,
    SetNotificationsEnabledAction,
    SetQuickActionsMenuAction,
    SetQuickActionsMenuActionVisible,
    SetQuotedMessageAction,
    SetSearchQueryAction,
    SetSearchResultsAction,
    SetShowMsgTimeAndStatusAction,
    SetSoundNotificationsEnabledAction,
    SetSwipedMessageAction,
    SetUnreadCounterAction,
    SetVibrationEnabledAction,
    SetWaitingForBotReplyAction,
    ViewImageAction,
    ViewImageListAction,
} from './chat.actions';

const getNotificationsEnabled = () => {
    const stored = localStorage.getItem(StorageKey.NotificationsEnabled);
    return stored && JSON.parse(stored) === true && Notification.permission === 'granted';
};

const getSoundNotificationsEnabled = () => {
    const stored = localStorage.getItem(StorageKey.SoundNotificationsEnabled);
    return stored ? JSON.parse(stored) === true : true;
};

const getHideMsgTimeAndStatus = () => {
    const stored = localStorage.getItem(StorageKey.HideMsgTimeAndStatus);
    return stored ? JSON.parse(stored) === true : false;
};

const getVibrationEnabled = () => {
    const stored = localStorage.getItem(StorageKey.VibrationEnabled);
    return stored ? JSON.parse(stored) === true : true;
};

const initialState = {
    open: false,
    fullscreen: false,
    activeTab: TabView.Chat,
    viewMode: ChatViewMode.Normal,
    chatInputBlurDelay: 0,
    overlay: null as { type: OverlayType; blurChat: boolean; props?: OverlayProps },
    agentTyping: false,
    unreadCounter: 0,
    viewImages: null as ImagesDisplayData,
    searchQuery: '',
    lastRequestedQuery: null as string,
    searchResults: null as string[],
    searchResultsWords: null as string[],
    searchResultIndex: 0,
    searchInProgress: false,
    hoveringButtons: null as HoveringButtonInfo[],
    isHoveringButtonsVisible: true,
    contextMenu: null as ContextMenuData,
    drawerData: null as DrawerData,
    quoteData: null as QuoteData,
    blinkMsg: null as { msgId: string; uuid: string },
    notificationsEnabled: getNotificationsEnabled(),
    soundNotificationsEnabled: getSoundNotificationsEnabled(),
    showMsgTimeAndStatus: !getHideMsgTimeAndStatus(),
    vibrationEnabled: getVibrationEnabled(),
    maximized: false,
    isScrolling: false,
    messagesHtmlRequest: null as { requestId: string; messages: MessageData[] },
    backgroundOperation: null as { desc: string; progress?: number },
    preAuthUnreadCounter: 0,
    quickActionsMenu: null as QuickActionButtonData[],
    waitingForBotReply: false,
    mediaGalleryInfo: null as { mediaInfo: DialogAggregateInfo; focusMediaType?: string },
    swipedMessage: null as MessageData,
    visilbeQAM: false,
};

export const chatReducer = (
    state = initialState,
    action: Action<ChatActionType>,
): typeof initialState => {
    switch (action.type) {
        case ChatActionType.SetOpen:
            const open = (action as SetChatOpenAction).payload;
            return { ...state, open };

        case ChatActionType.Toggle:
            return { ...state, open: !state.open };

        case ChatActionType.SetBlurDelay:
            const chatInputBlurDelay = (action as SetBlurDelay).payload;
            return { ...state, chatInputBlurDelay };

        case ChatActionType.FullScreen:
            const fullscreen = (action as SetChatFullScreenAction).payload;

            return { ...state, fullscreen };

        case ChatActionType.SetActiveTab:
            const tab = (action as SetActiveTabAction).payload;

            return { ...state, activeTab: tab };

        case ChatActionType.SetOverlay:
            const overlay = (action as SetChatOverlayAction).payload;
            return { ...state, overlay };

        case ChatActionType.SetAgentTyping:
            const agentTyping = (action as SetAgentTypingAction).payload;
            return { ...state, agentTyping };

        case ChatActionType.SetUnreadCounter:
            const unreadCounter = (action as SetUnreadCounterAction).payload;
            _counterLog.log(`ChatActionType.SetUnreadCounter`, `unreadCounter => ${unreadCounter}`);
            return { ...state, unreadCounter };

        case ChatActionType.ViewImage:
            const imgToView = (action as ViewImageAction).payload;

            return {
                ...state,
                viewImages: {
                    images: [imgToView],
                    currentIndex: 0,
                },
            };

        case ChatActionType.ViewImageList:
            const viewImages = (action as ViewImageListAction).payload;
            return {
                ...state,
                viewImages,
            };

        case ChatActionType.CloseImage:
            return { ...state, viewImages: null };

        case ChatActionType.SetViewMode:
            const { viewMode } = (action as SetChatViewModeAction).payload;
            return {
                ...state,
                viewMode,
                searchQuery: initialState.searchQuery,
                lastRequestedQuery: initialState.lastRequestedQuery,
                searchResults: initialState.searchResults,
                searchResultIndex: initialState.searchResultIndex,
            };

        case ChatActionType.SearchRequest:
            const { query } = (action as SearchRequestAction).payload;
            return {
                ...state,
                searchInProgress: true,
                lastRequestedQuery: query,
            };

        case ChatActionType.SetSearchQuery:
            const searchQuery = (action as SetSearchQueryAction).payload;
            return { ...state, searchQuery, searchResults: null };

        case ChatActionType.SetSearchResults: {
            const { msgIds: searchResults, words: searchResultsWords } = (
                action as SetSearchResultsAction
            ).payload;

            const searchResultIndex = searchResults ? Math.max(searchResults.length - 1, 0) : 0;

            return {
                ...state,
                searchResults,
                searchResultsWords,
                searchResultIndex,
                searchInProgress: false,
            };
        }

        case ChatActionType.CancelSearch: {
            const {
                searchInProgress,
                searchQuery,
                searchResultIndex,
                searchResults,
                searchResultsWords,
            } = initialState;

            return {
                ...state,
                searchInProgress,
                searchQuery,
                searchResultIndex,
                searchResults,
                searchResultsWords,
            };
        }

        case ChatActionType.GoToSearchResult:
            const searchResultIndex = (action as GoToSearchResultAction).payload;
            return {
                ...state,
                searchResultIndex,
            };

        case ChatActionType.SetHoveringButtons:
            const hoveringButtons = (action as SetHoveringButtonsAction).payload;
            return {
                ...state,
                hoveringButtons,
            };
        case ChatActionType.SetIsHoveringButtonsVisible:
            return {
                ...state,
                isHoveringButtonsVisible: (action as SetIsHoveringButtonsVisibleAction).payload,
            };

        case ChatActionType.CloseContextMenu:
            return {
                ...state,
                contextMenu: null,
            };

        case ChatActionType.OpenContextMenu:
        case ChatActionType.UpdateContextMenu:
            const contextMenu = (action as ContextMenuAction).payload;
            return {
                ...state,
                contextMenu,
            };

        case ChatActionType.SetDrawer:
            const drawerData = (action as DrawerAction).payload;
            return {
                ...state,
                drawerData,
            };

        case ChatActionType.SetQuotedMessage:
            const quoteData = (action as SetQuotedMessageAction).payload;
            return {
                ...state,
                quoteData,
            };

        case ChatActionType.BlinkMessage:
            const blinkMsg = (action as BlinkMessageAction).payload;
            return {
                ...state,
                blinkMsg,
            };

        case ChatActionType.SetNotificationsEnabled:
            const notificationsEnabled = (action as SetNotificationsEnabledAction).payload;
            return {
                ...state,
                notificationsEnabled,
            };

        case ChatActionType.SetSoundNotificationsEnabled:
            const soundNotificationsEnabled = (action as SetSoundNotificationsEnabledAction)
                .payload;
            return {
                ...state,
                soundNotificationsEnabled,
            };

        case ChatActionType.SetShowMsgTimeAndStatus:
            const showMsgTimeAndStatus = (action as SetShowMsgTimeAndStatusAction).payload;
            return {
                ...state,
                showMsgTimeAndStatus,
            };

        case ChatActionType.SetVibrationEnabled:
            const vibrationEnabled = (action as SetVibrationEnabledAction).payload;
            return {
                ...state,
                vibrationEnabled,
            };

        case ChatActionType.SetMaximized:
            const maximized = (action as SetChatMaximizedAction).payload;
            return {
                ...state,
                maximized,
            };

        case ChatActionType.CurrentHistoryHtmlRequest:
            const { requestId, messages } = (action as MessagesHtmlRequestAction).payload;
            return {
                ...state,
                messagesHtmlRequest: { requestId, messages },
            };

        case ChatActionType.CurrentHistoryHtmlResponse:
            return {
                ...state,
                messagesHtmlRequest: null,
            };

        case ChatActionType.SetBackgroundOperation:
            const backgroundOperation = (action as SetBackgroundOperationAction).payload;
            return {
                ...state,
                backgroundOperation,
            };

        case ChatActionType.SavePreAuthUnreadCounter:
            return {
                ...state,
                preAuthUnreadCounter: state.unreadCounter,
            };

        case ChatActionType.ResetPreAuthUnreadCounter:
            return {
                ...state,
                preAuthUnreadCounter: 0,
            };

        case ChatActionType.SetQuickActionsMenu:
            // const quickActionsMenu = (action as SetQuickActionsMenuAction).payload;
            return {
                ...state,
                // quickActionsMenu,
            };

        case ChatActionType.SetQuickActionsMenuReducer:
            const quickActionsMenu = (action as SetQuickActionsMenuAction).payload;
            return {
                ...state,
                quickActionsMenu,
            };

        case ChatActionType.SetQuickActionsMenuVisible:
            const visilbeQAM = (action as SetQuickActionsMenuActionVisible).payload;
            return {
                ...state,
                visilbeQAM,
            };

        case ChatActionType.SetWaitingForBotReply:
            const waitingForBotReply = (action as SetWaitingForBotReplyAction).payload;
            return {
                ...state,
                waitingForBotReply,
            };

        case ChatActionType.OpenMediaGallery:
            const mediaGalleryInfo = (action as OpenMediaGalleryAction).payload;
            return {
                ...state,
                viewMode: ChatViewMode.Gallery,
                mediaGalleryInfo,
            };

        case ChatActionType.SetSwipedMessage:
            const swipedMessage = (action as SetSwipedMessageAction).payload;

            return {
                ...state,
                swipedMessage,
            };

        case ChatActionType.SetIsScrolling:
            const { payload } = action as SetIsScrolling;

            return {
                ...state,
                isScrolling: payload,
            };
        default:
            return state;
    }
};
